import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, MapPin, ArrowRight, CheckCircle2, Sparkles, Shield, Clock, CreditCard } from "lucide-react";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import WhatsAppFAB from "@/components/shared/WhatsAppFAB";
import SEO from "@/components/shared/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { api, unwrap } from "@/lib/api";
import NotFoundPage from "@/pages/public/NotFoundPage";

const HERO_IMG = "https://images.pexels.com/photos/279746/pexels-photo-279746.jpeg";

export default function ZoneLandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [zone, setZone] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/zones/by-slug/${slug}`);
        setZone(unwrap(r));
      } catch {
        setNotFound(true);
      } finally { setLoading(false); }
    })();
  }, [slug]);

  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (notFound) return <NotFoundPage />;

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const schemaLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `Residential cleaning in ${zone.name}`,
    description: zone.seo_description || `Professional residential cleaning across ${zone.name}.`,
    provider: { "@id": `${origin}/#business`, "@type": "LocalBusiness", name: "Ever Clean Home" },
    areaServed: (zone.cities || []).map((c) => ({ "@type": "City", name: c })),
    offers: { "@type": "Offer", priceCurrency: "USD", url: `${origin}/book?zone=${zone.id}` },
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="zone-landing">
      <SEO
        title={`House Cleaning in ${zone.name} — Ever Clean Home`}
        description={zone.seo_description || `Trusted residential cleaning across ${zone.name}. Book online in 60 seconds — insured team, transparent pricing.`}
        jsonLd={[schemaLd]}
      />
      <Header />
      <WhatsAppFAB />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grain opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 md:px-8 pt-12 md:pt-20 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0EA5A4]/10 text-[#0F766E] text-xs font-semibold mb-6">
              <MapPin className="w-3.5 h-3.5" /> Serving {zone.name}
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95]">
              Cleaning services <br />in <span className="text-[#0EA5A4]">{zone.name}</span>
            </h1>
            <p className="mt-5 text-lg text-[#4B5563] max-w-xl leading-relaxed">
              {zone.tagline || `Trusted residential cleaning across ${zone.name}. Book online in 60 seconds.`}
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Button onClick={() => navigate(`/book?zone=${zone.id}`)} size="lg"
                className="bg-[#0EA5A4] hover:bg-[#0F766E] text-white rounded-xl px-7 h-14 text-base font-semibold shadow-lg shadow-[#0EA5A4]/20 hover:scale-[1.02] transition-transform"
                data-testid="zone-cta-primary">
                Book a cleaning in {zone.state} <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
            className="lg:col-span-5 relative">
            <div className="absolute -inset-5 bg-[#0EA5A4]/15 rounded-[2.5rem] -z-10 rotate-1" />
            <img src={HERO_IMG} alt={`Clean home in ${zone.name}`}
              className="rounded-3xl shadow-2xl w-full aspect-[4/5] object-cover" />
          </motion.div>
        </div>
      </section>

      {/* Cities + ZIPs */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-8 grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <h2 className="font-display text-3xl font-extrabold tracking-tighter">Cities we serve in {zone.name}</h2>
            <div className="mt-5 flex flex-wrap gap-2">
              {(zone.cities || []).map((c, i) => (
                <span key={i} className="px-3 py-1.5 rounded-full text-sm font-medium bg-[#0EA5A4]/10 text-[#0F766E]" data-testid={`zone-city-${i}`}>
                  {c}
                </span>
              ))}
            </div>
            <div className="mt-7 text-sm text-[#4B5563]">
              Don't see your town? <Link to="/track" className="text-[#0EA5A4] font-semibold hover:underline">Track a booking</Link> or <Link to="/contact" className="text-[#0EA5A4] font-semibold hover:underline">contact us</Link> — we expand often.
            </div>
          </div>
          <div>
            <h2 className="font-display text-3xl font-extrabold tracking-tighter">Active ZIP codes</h2>
            <div className="mt-5 grid grid-cols-3 sm:grid-cols-4 gap-2 font-mono text-xs">
              {(zone.zip_codes || []).map((z, i) => (
                <span key={i} className="px-2.5 py-1.5 rounded-lg border border-[#E5E7EB] text-center" data-testid={`zone-zip-${i}`}>
                  {z}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="font-display text-3xl font-extrabold tracking-tighter text-center">Why {zone.name} chooses us</h2>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Sparkles, t: "15+ years experience", d: "Cleaning Boston-area homes since 2011." },
              { icon: Shield, t: "Insured & LLC", d: "Fully insured and background-checked." },
              { icon: CheckCircle2, t: "100% guarantee", d: "Not happy? We re-clean it within 24h." },
              { icon: Clock, t: "Online booking", d: "60-second checkout. No phone calls." },
            ].map((it, i) => (
              <Card key={i} className="rounded-2xl p-5 border border-[#E5E7EB] text-center">
                <span className="w-11 h-11 rounded-xl bg-[#0EA5A4]/10 text-[#0F766E] grid place-items-center mx-auto mb-3"><it.icon className="w-5 h-5" /></span>
                <div className="font-display font-bold">{it.t}</div>
                <p className="text-xs text-[#4B5563] mt-1">{it.d}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 mb-10">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="rounded-[2.5rem] bg-[#0EA5A4] text-white p-10 md:p-14 grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            <div>
              <h3 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter">Ready, {zone.state}?</h3>
              <p className="mt-2 text-white/85 max-w-md">Get an instant quote tailored to your home in {zone.name}.</p>
            </div>
            <div className="md:text-right">
              <Button onClick={() => navigate(`/book?zone=${zone.id}`)} size="lg"
                className="bg-[#FACC15] text-[#0C1A1A] hover:bg-[#FACC15]/90 rounded-xl h-14 px-7 font-semibold hover:scale-[1.02] transition-transform"
                data-testid="zone-cta-final">
                Book a cleaning <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
